/**
 * useWebSocket — generic, feature-agnostic WebSocket hook.
 *
 * Ported from the legacy `3mote-fe/src/utils/hooks/useWebSocket.js` but
 * rewritten in TS, narrowed to the new-stack realtime contract (no
 * `laravel-echo`, no AWS Amplify, no client-type init handshake — the
 * Phase 3 BE doesn't need it).
 *
 * Behavior:
 *   - Opens `new WebSocket(`${url}?token=${token}`)` on mount, or whenever
 *     `enabled || token || url` changes.
 *   - Auto-reconnects on close with exponential backoff
 *     (1s, 2s, 4s, 8s, 16s, capped at 30s). Resets on a successful open.
 *   - **Stays connected while the tab is hidden** (background tab,
 *     unfocused window, OS-level minimize). This is load-bearing for
 *     the presence model: presence = "user has ≥1 active WS
 *     connection in this tenant" (ADR-028). Pre-2026-05-12 this hook
 *     force-closed on `visibilitychange === 'hidden'` to save battery;
 *     that broke presence — every other tab on the user's machine saw
 *     them flip offline the moment they alt-tabbed away. Real tab
 *     close still propagates correctly (browser closes the WS on
 *     `unload`, BE `$disconnect` fires). Idle WS in a hidden tab is a
 *     few KB of memory; not worth the broken UX.
 *   - Heartbeat: sends `{ type: 'ping' }` every 30s; if no `pong` (or any
 *     server message) lands within 35s, force-closes and reconnects.
 *     Hidden tabs have their setInterval throttled by the browser
 *     (≥1 min minimum), so a long-hidden tab will eventually trip API
 *     Gateway's 10-min idle close — the reconnect lifecycle handles
 *     this transparently when the tab returns to foreground.
 *   - On unmount: clean close, no further reconnects.
 *   - Auth-derived close codes (4401 / 4403) bubble up via `onAuthFailure`
 *     so callers can refresh the Cognito token and reconnect with a fresh
 *     `token` prop. (Cognito access tokens expire after 1h.)
 *   - StrictMode double-mount safe: refs guard the live socket and pending
 *     reconnect timer so a remount doesn't double-connect.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export type WebSocketStatus = 'connecting' | 'open' | 'closed' | 'reconnecting' | 'idle'

export interface ServerMessage {
  type: string
  [key: string]: unknown
}

export interface UseWebSocketOptions {
  /** Base WebSocket URL, e.g. `wss://realtime-staging.3mote.com`. */
  url: string
  /**
   * Cognito access token. Hook attaches as `?token=...`. Captured by
   * ref at connect-time only — a mid-session token rotation does NOT
   * trigger a reconnect, because the BE only validates the token at
   * `$connect`. The latest token rides the next natural reconnect
   * (idle-resume, scheduled retry, etc.). See ADR-033.
   */
  token: string | null
  /**
   * Tenant subdomain (same source as the `X-Tenant-Subdomain` HTTP
   * header). Required — the BE `$connect` handler 401s
   * without it. The hook appends `?tenant=<subdomain>` alongside
   * `?token=...`. When omitted (or empty) the WS connect is skipped
   * entirely; the connection would fail at the BE anyway.
   */
  tenant?: string | null
  /** Called for every server message. */
  onMessage: (message: ServerMessage) => void
  /** Optional: skip the connect entirely (e.g. user is signed out). Defaults to true. */
  enabled?: boolean
  /**
   * Optional: called when the server closes with a Cognito-derived auth
   * failure code (4401 / 4403). Caller is expected to refresh the token
   * and feed a new `token` prop in to reconnect, or bounce to /login.
   */
  onAuthFailure?: (closeCode: number) => void
  /**
   * Optional: when `true`, the hook closes any live socket cleanly with
   * code 1000 and SUPPRESSES the normal close-driven reconnect path.
   * Status becomes `'idle'`. Flipping back to `false` kicks a fresh
   * connect attempt with the latest token. Used by `<WebSocketProvider>`
   * to implement idle-disconnect (ADR-033) — close after N min of no
   * user activity, reopen on the next interaction.
   */
  suspended?: boolean
}

export interface UseWebSocketResult {
  status: WebSocketStatus
  send: (message: object) => void
}

const HEARTBEAT_INTERVAL_MS = 30_000
const HEARTBEAT_TIMEOUT_MS = 35_000
const INITIAL_BACKOFF_MS = 1_000
const MAX_BACKOFF_MS = 30_000

/** Cognito-style auth-failure close codes. */
const AUTH_FAILURE_CODES = new Set([4401, 4403])

export function useWebSocket(options: UseWebSocketOptions): UseWebSocketResult {
  const {
    url,
    token,
    tenant,
    onMessage,
    enabled = true,
    onAuthFailure,
    suspended = false,
  } = options

  const [status, setStatus] = useState<WebSocketStatus>('closed')

  // Refs for the live socket + timers so React 18 strict-mode double-mount
  // can be deduped without ripping the connection out from under the user.
  const socketRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const heartbeatTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  // Diagnostic: when the live socket actually opened (ms epoch). Used to
  // compute `timeOpenMs` on close so the close-event log shows how long
  // the socket lasted. Reset to 0 when no socket is alive.
  const openedAtRef = useRef(0)
  // `unmountedRef` short-circuits the close handler's reconnect logic when
  // the cleanup function is the cause of the close, not the network.
  const unmountedRef = useRef(false)
  // Token captured via ref so the connect-effect doesn't re-fire on every
  // Cognito access-token rotation. Connect-time reads `tokenRef.current`
  // for the freshest value. Per ADR-033.
  const tokenRef = useRef(token)
  // Mirrors the `suspended` option so the close handler (whose closure was
  // captured at connect-time) can see the current value when deciding
  // whether to schedule a reconnect.
  const suspendedRef = useRef(suspended)
  // Stable refs for callbacks so the connect closure doesn't churn on
  // every parent render.
  const onMessageRef = useRef(onMessage)
  const onAuthFailureRef = useRef(onAuthFailure)

  useEffect(() => {
    onMessageRef.current = onMessage
  }, [onMessage])
  useEffect(() => {
    onAuthFailureRef.current = onAuthFailure
  }, [onAuthFailure])
  useEffect(() => {
    tokenRef.current = token
  }, [token])
  useEffect(() => {
    suspendedRef.current = suspended
  }, [suspended])

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }
  }, [])

  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current !== null) {
      clearInterval(heartbeatIntervalRef.current)
      heartbeatIntervalRef.current = null
    }
    if (heartbeatTimeoutRef.current !== null) {
      clearTimeout(heartbeatTimeoutRef.current)
      heartbeatTimeoutRef.current = null
    }
  }, [])

  const closeSocket = useCallback(() => {
    clearHeartbeat()
    if (socketRef.current) {
      try {
        socketRef.current.close()
      } catch {
        // ignore — socket was likely already closing.
      }
      socketRef.current = null
    }
  }, [clearHeartbeat])

  const computeBackoffMs = useCallback((attempt: number): number => {
    const exp = INITIAL_BACKOFF_MS * 2 ** attempt
    return Math.min(exp, MAX_BACKOFF_MS)
  }, [])

  // The full connect lifecycle. Re-defined whenever its inputs change so
  // the closure always captures fresh `url` / `tenant` / `enabled`. The
  // token is read at connect-time from `tokenRef.current` so that a
  // mid-session Cognito refresh doesn't churn the connection — per
  // ADR-033.
  const connect = useCallback(() => {
    if (unmountedRef.current) return
    if (suspendedRef.current) return
    const currentToken = tokenRef.current
    if (!enabled || !currentToken || !url) return
    // Note: we DO connect when the page is hidden — presence depends on
    // the WS staying open across alt-tabs and unfocused windows (see
    // ADR-028 + the hook-level note about pre-2026-05-12 behavior).
    // Idle-disconnect (ADR-033) layers on top of this: the provider
    // flips `suspended` after N min of no activity (hidden or not),
    // which is the path that actually closes the socket. A hidden tab
    // with no activity goes idle and disconnects; a hidden tab with
    // activity (e.g., the user has another foreground tab and visits
    // back briefly) stays open.

    // Tear down any previous socket / timers before standing up a new one.
    clearReconnectTimer()
    closeSocket()

    setStatus('connecting')

    // `?tenant=<subdomain>` rides alongside `?token=<jwt>`.
    // Both params are required by the BE `$connect` handler. Skip the
    // tenant param entirely when caller doesn't supply one (legacy
    // call sites in older agent branches); the BE will reject the
    // connect with 4401 in that case, which the auth-failure path
    // handles. Once every call site is migrated this fallback can go.
    const tenantParam =
      typeof tenant === 'string' && tenant.length > 0 ? `&tenant=${encodeURIComponent(tenant)}` : ''
    const wsUrl = `${url}?token=${encodeURIComponent(currentToken)}${tenantParam}`
    let ws: WebSocket
    try {
      ws = new WebSocket(wsUrl)
    } catch {
      // Invalid URL or browser refused — schedule a retry.
      scheduleReconnect()
      return
    }
    socketRef.current = ws

    // Heartbeat timeout — armed initially when the socket opens, re-armed
    // on every incoming server message. Fires if HEARTBEAT_TIMEOUT_MS
    // passes with no message (no pong, no broadcast); closes the socket
    // with code 1005 so the close handler can schedule a reconnect.
    // Shared between the 'open' and 'message' listeners.
    const armHeartbeatTimeout = () => {
      if (heartbeatTimeoutRef.current !== null) {
        clearTimeout(heartbeatTimeoutRef.current)
      }
      heartbeatTimeoutRef.current = setTimeout(() => {
        if (socketRef.current === ws) {
          try {
            ws.close()
          } catch {
            // ignore
          }
        }
      }, HEARTBEAT_TIMEOUT_MS)
    }

    ws.addEventListener('open', () => {
      if (socketRef.current !== ws) return
      reconnectAttemptsRef.current = 0
      openedAtRef.current = Date.now()
      setStatus('open')

      // Heartbeat: send ping every 30s; the armHeartbeatTimeout above
      // expects any message back within HEARTBEAT_TIMEOUT_MS.
      heartbeatIntervalRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ type: 'ping' }))
          } catch {
            // ignore — the close handler will fire shortly.
          }
          armHeartbeatTimeout()
        }
      }, HEARTBEAT_INTERVAL_MS)
      armHeartbeatTimeout()
    })

    ws.addEventListener('message', (event) => {
      if (socketRef.current !== ws) return
      // Any server frame implies the link is alive — reset the heartbeat
      // timeout. The interval keeps running.
      armHeartbeatTimeout()

      let parsed: unknown
      try {
        parsed = JSON.parse(typeof event.data === 'string' ? event.data : '')
      } catch {
        return
      }
      if (
        typeof parsed !== 'object' ||
        parsed === null ||
        typeof (parsed as { type?: unknown }).type !== 'string'
      ) {
        return
      }
      const message = parsed as ServerMessage
      // `pong` is a transport-layer concern — don't bubble to the caller.
      if (message.type === 'pong') return
      onMessageRef.current(message)
    })

    ws.addEventListener('error', () => {
      // The browser fires `error` before `close` on most failures; the
      // close handler does the reconnect bookkeeping. Just drop the
      // status here so consumers see the disconnect immediately.
      if (socketRef.current === ws) {
        setStatus('reconnecting')
      }
    })

    ws.addEventListener('close', (event) => {
      if (socketRef.current !== ws) return
      const timeOpenMs = openedAtRef.current === 0 ? 0 : Date.now() - openedAtRef.current
      socketRef.current = null
      openedAtRef.current = 0
      clearHeartbeat()

      if (unmountedRef.current) return

      // Idle-disconnect path: provider flipped `suspended` true and we
      // closed the socket ourselves. The status is already (or about
      // to be) `'idle'` from the suspend effect; no reconnect, no
      // auth-failure handoff.
      if (suspendedRef.current) {
        setStatus('idle')
        return
      }

      if (AUTH_FAILURE_CODES.has(event.code)) {
        // Hand off to the caller: typical response is to refresh the
        // Cognito token (which feeds a new `token` prop) or bounce to
        // /login. Don't auto-reconnect with the bad token.
        setStatus('closed')
        onAuthFailureRef.current?.(event.code)
        return
      }

      // Failed BEFORE the open event fired (handshake rejected by API
      // Gateway / BE `$connect`). The single most common cause on this
      // stack is a stale Cognito access token in the WS URL: when a
      // heartbeat-driven reconnect happens to fire around the hour
      // boundary, `tokenRef.current` still holds the about-to-expire
      // token and the BE rejects the handshake with 401, which API
      // Gateway surfaces as a 1006 close on the client. The 5-min
      // refresh-tick will eventually catch up and rotate the token,
      // but that's a long wait — proactively hand off to the auth-
      // failure callback so the provider's `refresh()` fires now. The
      // refresh resolves while the standard scheduleReconnect backoff
      // is running; by the 1-2s mark the new token is in tokenRef and
      // the next attempt succeeds. Falls through to scheduleReconnect
      // (unlike the 4401/4403 path which bails) because we *want* the
      // retry — we just want it with a fresh token. See ADR-033
      // amendment 2026-06-01.
      if (event.code === 1006 && timeOpenMs === 0) {
        onAuthFailureRef.current?.(event.code)
      }

      scheduleReconnect()
    })
  }, [url, tenant, enabled, clearReconnectTimer, closeSocket, clearHeartbeat])

  // `scheduleReconnect` lives outside `connect` so the close handler can
  // call it without forming a callback cycle. It defers to `connectRef`
  // which is kept up-to-date by the next effect.
  const connectRef = useRef(connect)
  useEffect(() => {
    connectRef.current = connect
  }, [connect])

  function scheduleReconnect() {
    if (unmountedRef.current) return
    setStatus('reconnecting')
    const delay = computeBackoffMs(reconnectAttemptsRef.current)
    reconnectAttemptsRef.current += 1
    clearReconnectTimer()
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      connectRef.current()
    }, delay)
  }

  // Main connect lifecycle. Fires on mount and any time inputs change.
  // `suspended` is a dep so the effect re-runs on idle-suspend / resume
  // transitions: on suspend the cleanup closes the live socket cleanly,
  // and the next effect body short-circuits the connect; on resume the
  // body fires `connect()` afresh with the latest token from
  // `tokenRef.current`.
  useEffect(() => {
    unmountedRef.current = false
    reconnectAttemptsRef.current = 0
    if (suspended) {
      // Close any in-flight socket (the close handler bails on
      // `suspendedRef.current`, so no reconnect is scheduled) and
      // mark status accordingly.
      if (socketRef.current) {
        try {
          socketRef.current.close(1000, 'idle')
        } catch {
          // ignore
        }
      }
      setStatus('idle')
    } else {
      connect()
    }

    return () => {
      unmountedRef.current = true
      clearReconnectTimer()
      closeSocket()
      setStatus('closed')
    }
  }, [connect, clearReconnectTimer, closeSocket, suspended])

  // Visibility handling: on `visible`, if the socket happens to be
  // closed (e.g. API Gateway's 10-min idle close trimmed it while the
  // tab was hidden), kick a reconnect attempt right away rather than
  // waiting for the next exponential-backoff tick. We do NOT force-
  // close on `hidden` — see the hook-level docstring for the presence
  // rationale (ADR-028).
  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    const handleVisibility = () => {
      if (document.visibilityState === 'visible' && enabled && token && url && !socketRef.current) {
        clearReconnectTimer()
        reconnectAttemptsRef.current = 0
        connectRef.current()
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [enabled, token, url, clearReconnectTimer])

  const send = useCallback((message: object) => {
    const ws = socketRef.current
    if (!ws || ws.readyState !== WebSocket.OPEN) return
    try {
      ws.send(JSON.stringify(message))
    } catch {
      // ignore — the close handler will fire shortly.
    }
  }, [])

  return { status, send }
}
